import React, { useState } from "react";

/**
 * Vertical tab strip with a right-side panel on desktop, collapses to an
 * accordion on mobile (< md). Used by the Our Story page for both the
 * Company Story section and the Leadership section.
 *
 * Tabs prop shape:
 *   [{ id: string, label: string, render: (active: boolean) => ReactNode }]
 *
 * Props:
 *   - defaultId: id of the tab open by default
 *   - testIdPrefix: used to derive data-testid on tab buttons + panels
 *   - alignContent: "top" | "bottom" — desktop only. Controls vertical
 *     placement of the right-side panel relative to the tab stack.
 *     "bottom" anchors the panel's bottom edge to the bottom of the tab
 *     stack (matches the request to "bottom-align card text with tabs").
 */
export default function VerticalTabs({
  tabs,
  defaultId,
  testIdPrefix,
  alignContent = "bottom",
}) {
  const [activeId, setActiveId] = useState(defaultId || tabs[0]?.id);
  const active = tabs.find((t) => t.id === activeId) || tabs[0];

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 md:gap-10">
      {/* Outer grid keeps the default `stretch` alignment so both columns
          share the row height. Vertical placement of the panel content is
          handled INSIDE the panel stack (see justifyContent below); this
          way the tab column always starts at the top of the row — under
          the section's H2 — instead of awkwardly floating at the bottom. */}
      {/* Tab list — vertical stack. On mobile, each tab is followed by an
          inline panel when active (accordion behaviour). On desktop, only
          the labels render here; content is shown in the right column.
          The column uses justify-content from alignContent so the whole
          stack can sit at top / center / bottom of the row, mirroring the
          right-side panel's alignment. */}
      <div
        className="md:col-span-4"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          justifyContent:
            alignContent === "bottom"
              ? "flex-end"
              : alignContent === "center"
              ? "center"
              : "flex-start",
        }}
      >
        {tabs.map((t) => {
          const isActive = t.id === activeId;
          return (
            <React.Fragment key={t.id}>
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`${testIdPrefix}-tab-${t.id}`}
                data-active={isActive ? "true" : "false"}
                onClick={() =>
                  setActiveId((cur) => (cur === t.id ? cur : t.id))
                }
                className="vt-tab-button"
                style={{
                  textAlign: "left",
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  background: isActive
                    ? "var(--color-surface-container-lowest)"
                    : "transparent",
                  border: "none",
                  borderLeft: `3px solid ${
                    isActive
                      ? "var(--color-tertiary)"
                      : "var(--color-outline-variant, rgba(4,13,35,0.12))"
                  }`,
                  cursor: "pointer",
                  color: isActive
                    ? "var(--color-on-surface)"
                    : "rgba(4,13,35,0.38)",
                  fontFamily: "var(--font-display)",
                  fontSize: "clamp(1.0625rem, 1.3vw, 1.25rem)",
                  fontWeight: isActive ? 700 : 500,
                  letterSpacing: "-0.01em",
                  transition:
                    "background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease",
                }}
              >
                <span
                  aria-hidden
                  className="vt-chev"
                  style={{
                    color: "var(--color-tertiary)",
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "1.05em",
                    letterSpacing: "-0.04em",
                    transform: isActive ? "translateX(0)" : "translateX(-2px)",
                    transition: "transform 0.18s ease",
                  }}
                >
                  »
                </span>
                <span style={{ flex: 1 }}>{t.label}</span>
              </button>

              {/* Mobile inline panel — shown only when active, only below md */}
              {isActive && (
                <div
                  className="md:hidden"
                  data-testid={`${testIdPrefix}-panel-${t.id}`}
                  style={{
                    padding: "18px 18px 24px",
                    background: "var(--color-surface-container-lowest)",
                    borderLeft: "3px solid var(--color-tertiary)",
                    animation: "fadeUp 0.3s ease",
                  }}
                >
                  {t.render(true)}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Desktop panel area — STACK all panels in the same grid cell so the
          section's vertical extent stays stable across tab switches. The
          cell sizes itself to the tallest panel; the active one becomes
          visible, others are visually hidden but still reserve layout. */}
      <div className="hidden md:block md:col-span-7 md:col-start-6">
        <div
          style={{
            display: "grid",
            gridTemplateAreas: '"stack"',
            // Fill the row height so child panels can vertically align
            // against the (often taller) tabs column when alignContent
            // !== "top". Without this, the stack auto-sizes to its
            // tallest panel and centering becomes a no-op.
            height: "100%",
          }}
        >
          {tabs.map((t) => {
            const isActive = t.id === activeId;
            return (
              <div
                key={t.id}
                data-testid={`${testIdPrefix}-panel-${t.id}`}
                aria-hidden={!isActive}
                style={{
                  gridArea: "stack",
                  visibility: isActive ? "visible" : "hidden",
                  opacity: isActive ? 1 : 0,
                  transition: "opacity 0.28s ease",
                  pointerEvents: isActive ? "auto" : "none",
                  // Bottom-align panel content within the auto-sized stack
                  // cell when the section requests bottom alignment, so
                  // short panels (e.g. "Always" with 2 paragraphs) sit at
                  // the bottom edge rather than floating at the top with
                  // a large gap below.
                  display: "flex",
                  flexDirection: "column",
                  justifyContent:
                    alignContent === "bottom"
                      ? "flex-end"
                      : alignContent === "center"
                      ? "center"
                      : "flex-start",
                }}
              >
                {t.render(isActive)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
